import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Chip, Grid} from 'views/components';
import {Translate, translate} from 'views/module';
import {Assignment} from 'views/icons';

const Requirements = (props) => {
  const {newJob, updateJobRequirements, setIsStepValid} = props;

  const requirements = newJob.requirements || {
    certifications: [],
    atLeastOneCertifications: [],
    niceToHaveCertifications: [],
    languages: [],
    niceToHaveLanguages: [],
  };

  const selectedCertifications = requirements.certifications || [];
  const atLeastOneCertifications = requirements.atLeastOneCertifications || [];
  const niceToHaveCertifications = requirements.niceToHaveCertifications || [];

  const selectedLanguages = requirements.languages || [];
  const niceToHaveLanguages = requirements.niceToHaveLanguages || [];

  // Certification options array - get keys from translation object
  const certificationOptionsObject = translate('career.create.Requirements.CertificationsOptions') || {};
  const certificationOptions = Object.keys(certificationOptionsObject);

  // Language options array - get keys from translation object
  const languageOptionsObject = translate('career.create.Requirements.LanguageOptions') || {};
  const languageOptions = Object.keys(languageOptionsObject);

  // Step validity
  React.useEffect(() => {
    // At least one certification array must have values
    const hasMandatory = selectedCertifications.length >= 1;
    const hasAtLeastOne = atLeastOneCertifications.length >= 1;
    const hasNiceToHave = niceToHaveCertifications.length >= 1;

    const isValid = hasMandatory || hasAtLeastOne || hasNiceToHave;

    setIsStepValid(isValid);
  }, [selectedCertifications.length, atLeastOneCertifications.length,
    niceToHaveCertifications.length, setIsStepValid]);

  const handleCertificationToggle = (certification) => {
    updateJobRequirements((requirements) => {
      const mandatory = requirements.certifications || [];
      const atLeastOne = requirements.atLeastOneCertifications || [];
      const niceToHave = requirements.niceToHaveCertifications || [];

      // Cycle: none → mandatory → atLeastOne → niceToHave → none
      if (mandatory.includes(certification)) {
        return {
          ...requirements,
          certifications: mandatory.filter((c) => c !== certification),
          atLeastOneCertifications: [...atLeastOne, certification],
        };
      } else if (atLeastOne.includes(certification)) {
        return {
          ...requirements,
          atLeastOneCertifications: atLeastOne.filter((c) => c !== certification),
          niceToHaveCertifications: [...niceToHave, certification],
        };
      } else if (niceToHave.includes(certification)) {
        return {
          ...requirements,
          niceToHaveCertifications: niceToHave.filter((c) => c !== certification),
        };
      } else {
        return {
          ...requirements,
          certifications: [...mandatory, certification],
        };
      }
    });
  };

  const handleLanguageToggle = (language) => {
    updateJobRequirements((requirements) => {
      const mandatory = requirements.languages || [];
      const niceToHave = requirements.niceToHaveLanguages || [];

      // Cycle: none → mandatory → niceToHave → none
      if (mandatory.includes(language)) {
        return {
          ...requirements,
          languages: mandatory.filter((l) => l !== language),
          niceToHaveLanguages: [...niceToHave, language],
        };
      } else if (niceToHave.includes(language)) {
        return {
          ...requirements,
          niceToHaveLanguages: niceToHave.filter((l) => l !== language),
        };
      } else {
        return {
          ...requirements,
          languages: [...mandatory, language],
        };
      }
    });
  };

  const getSelectedCertificationLabels = (certs) => {
    return certs.map((cert) =>
      translate(`career.create.Requirements.CertificationsOptions.${cert}`),
    );
  };

  const getSelectedLanguageLabels = (langs) => {
    return langs.map((lang) =>
      translate(`career.create.Requirements.LanguageOptions.${lang}`),
    );
  };

  // Check for bottom summary display
  const hasRequirements = selectedCertifications.length > 0 || atLeastOneCertifications.length > 0 ||
    niceToHaveCertifications.length > 0 || selectedLanguages.length > 0 || niceToHaveLanguages.length > 0;

  return (
    <Box sx={{p: 2, maxWidth: 1200, mx: 'auto'}}>
      <Typography variant="h4" gutterBottom sx={{textAlign: 'center', mb: 4}}>
        <Translate expression="career.create.Requirements.Title" />
      </Typography>

      {/* Color Key */}
      <Box sx={{mb: 4, display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap'}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Chip label={translate('career.create.Requirements.ColorKeyMandatory')} color="primary" />
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Chip label={translate('career.create.Requirements.ColorKeyAtLeastOne')} color="secondary" />
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Chip label={translate('career.create.Requirements.ColorKeyNiceToHave')} color="warning" />
        </Box>
      </Box>

      {/* Certifications Selection */}
      <Box sx={{mb: 4, textAlign: 'center'}}>
        <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 500}}>
          <Translate expression="career.create.Requirements.CertificationsLabel" />
          <Typography component="span" sx={{color: 'error.main', ml: 0.5}}>*</Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
          <Translate expression="career.create.Requirements.CertificationsDescription" />
        </Typography>

        {/* Certification Options Grid */}
        <Grid container spacing={1.5} justifyContent="center">
          {
            certificationOptions.map((option) => {
              const isMandatory = selectedCertifications.includes(option);
              const isAtLeastOne = atLeastOneCertifications.includes(option);
              const isNiceToHave = niceToHaveCertifications.includes(option);
              const label = translate(`career.create.Requirements.CertificationsOptions.${option}`);

              return (
                <Grid item key={option}>
                  <Chip
                    label={label}
                    onClick={() => handleCertificationToggle(option)}
                    color={isMandatory ? 'primary' : isAtLeastOne ? 'secondary' : isNiceToHave ? 'warning' : 'default'}
                    variant={isMandatory || isAtLeastOne || isNiceToHave ? 'filled' : 'outlined'}
                    sx={{
                      '&:hover': {
                        backgroundColor: isMandatory ? 'primary.main' : isAtLeastOne ? 'secondary.main' : isNiceToHave ? 'warning.main' : 'default',
                      },
                    }}
                  />
                </Grid>
              );
            })
          }
        </Grid>
      </Box>

      {/* Languages Selection */}
      <Box sx={{mb: 4, textAlign: 'center'}}>
        <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 500}}>
          <Translate expression="career.create.Requirements.LanguagesLabel" />
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
          <Translate expression="career.create.Requirements.LanguagesDescription" />
        </Typography>

        {/* Language Options Grid */}
        <Grid container spacing={1.5} justifyContent="center">
          {
            languageOptions.map((option) => {
              const isMandatory = selectedLanguages.includes(option);
              const isNiceToHave = niceToHaveLanguages.includes(option);
              const label = translate(`career.create.Requirements.LanguageOptions.${option}`);

              return (
                <Grid item key={option}>
                  <Chip
                    label={label}
                    onClick={() => handleLanguageToggle(option)}
                    color={isMandatory ? 'primary' : isNiceToHave ? 'warning' : 'default'}
                    variant={isMandatory || isNiceToHave ? 'filled' : 'outlined'}
                    sx={{
                      '&:hover': {
                        backgroundColor: isMandatory ? 'primary.main' : isNiceToHave ? 'warning.main' : 'default',
                      },
                    }}
                  />
                </Grid>
              );
            })
          }
        </Grid>
      </Box>

      {/* Requirements Summary */}
      {
        hasRequirements &&
        <Box sx={{maxWidth: 900, mx: 'auto'}}>
          {/* Summary box */}
          <Box
            sx={{
              p: 3,
              backgroundColor: '#f8fafc',
              borderRadius: 2,
              border: '1px solid #e2e8f0',
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 3}}>
              <Assignment sx={{color: '#64748b'}} />
              <Typography variant="subtitle2" sx={{fontWeight: 600, color: '#334155'}}>
                <Translate expression="career.create.Requirements.SelectedRequirementsTitle" />
              </Typography>
            </Box>

            {/* Certifications Section */}
            {
              (selectedCertifications.length > 0 || atLeastOneCertifications.length > 0 || niceToHaveCertifications.length > 0) &&
              <Box sx={{mb: 3}}>
                <Typography variant="body1" sx={{fontWeight: 600, mb: 2, color: '#334155'}}>
                  <Translate expression="career.create.Requirements.CertificationLabel" />
                </Typography>

                {/* Mandatory Certifications */}
                {
                  selectedCertifications.length > 0 &&
                  <Box sx={{mb: 2, ml: 2}}>
                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5, color: 'primary.main'}}>
                      {translate('career.create.Requirements.ColorKeyMandatory')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getSelectedCertificationLabels(selectedCertifications).join(', ')}
                    </Typography>
                  </Box>
                }

                {/* At Least One Certifications */}
                {
                  atLeastOneCertifications.length > 0 &&
                  <Box sx={{mb: 2, ml: 2}}>
                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5, color: 'secondary.main'}}>
                      {translate('career.create.Requirements.ColorKeyAtLeastOne')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getSelectedCertificationLabels(atLeastOneCertifications).join(', ')}
                    </Typography>
                  </Box>
                }

                {/* Nice to Have Certifications */}
                {
                  niceToHaveCertifications.length > 0 &&
                  <Box sx={{ml: 2}}>
                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5, color: 'warning.main'}}>
                      {translate('career.create.Requirements.ColorKeyNiceToHave')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getSelectedCertificationLabels(niceToHaveCertifications).join(', ')}
                    </Typography>
                  </Box>
                }
              </Box>
            }

            {/* Languages Section */}
            {
              (selectedLanguages.length > 0 || niceToHaveLanguages.length > 0) &&
              <Box>
                <Typography variant="body1" sx={{fontWeight: 600, mb: 2, color: '#334155'}}>
                  <Translate expression="career.create.Requirements.LanguagesDisplayLabel" />
                </Typography>

                {/* Mandatory Languages */}
                {
                  selectedLanguages.length > 0 &&
                  <Box sx={{mb: 2, ml: 2}}>
                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5, color: 'primary.main'}}>
                      {translate('career.create.Requirements.ColorKeyMandatory')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getSelectedLanguageLabels(selectedLanguages).join(', ')}
                    </Typography>
                  </Box>
                }

                {/* Nice to Have Languages */}
                {
                  niceToHaveLanguages.length > 0 &&
                  <Box sx={{ml: 2}}>
                    <Typography variant="body2" sx={{fontWeight: 600, mb: 0.5, color: 'warning.main'}}>
                      {translate('career.create.Requirements.ColorKeyNiceToHave')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getSelectedLanguageLabels(niceToHaveLanguages).join(', ')}
                    </Typography>
                  </Box>
                }
              </Box>
            }
          </Box>
        </Box>
      }
    </Box>
  );
};

Requirements.propTypes = {
  newJob: PropTypes.object.isRequired,
  updateJobRequirements: PropTypes.func.isRequired,
  setIsStepValid: PropTypes.func.isRequired,
};

export default Requirements;
